import type { MouseEvent } from "react";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { shortMonthAndYear, formatDate } from "@similarweb/pro-logic-components";

import { ChangePercentage, TrafficShareWithVisits } from "app/components/React/Table/cells";
import { translate } from "app/filters/i18nFilter";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { PeriodOverPeriodService } from "app/services/PeriodOverPeriodService";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";

import type { DemandAnalysisSearchTrendsQueryParams } from "../../types";

import type { KeywordListsComparisonTableRow } from "./keywordGroupsTableDataSelector";

export interface KeywordGroupTableRow extends KeywordListsComparisonTableRow {
    color: string;
}

type KeywordTrendsTableColumn = ColumnsType<KeywordGroupTableRow>[number];

const keywordGroupName: KeywordTrendsTableColumn = {
    dataIndex: "keywordGroupName",
    get title() {
        return translate("demand.analysis.search.trends.breakdown.term.lists");
    },
    tooltipTitle: "Keyword",
    get tooltipSubtitle() {
        return translate("demand.analysis.search.trends.breakdown.search.term.tooltip");
    },
    render: (value: string | number | undefined, row) => {
        const destinationState = KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE;
        const currentParams = proNavigator.getParams() as DemandAnalysisSearchTrendsQueryParams;

        const newParams: DemandAnalysisSearchTrendsQueryParams = {
            ...currentParams,
            id: [row.keywordListId],
        };

        const url = proNavigator.getStateUrl(destinationState, newParams);

        const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
            const modifierKey = e.button >= 1 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;
            if (!e.defaultPrevented && !modifierKey) {
                e.preventDefault();
                void proNavigator.go(destinationState, newParams, { inherit: false, reload: true });
            }
        };

        return (
            <a
                href={url}
                onClick={handleClick}
            >
                {value}
            </a>
        );
    },
    sorter: (a, b) => {
        return a.keywordGroupName.localeCompare(b.keywordGroupName);
    },
};

const previousPeriodVolumeShare = (from: string, to: string): KeywordTrendsTableColumn => ({
    dataIndex: "previousShare",
    get title() {
        return translate("demand.analysis.search.trends.breakdown.volume.title", { from, to });
    },
    tooltipTitle: translate("demand.analysis.search.trends.breakdown.volume.share"),
    get tooltipSubtitle() {
        return translate("demand.analysis.search.trends.breakdown.volume.share.tooltip");
    },
    render: (value, row) => (
        <TrafficShareWithVisits
            isPreviousPeriod
            row={row}
            value={value}
            totalVisitsRowProp="previousVolume"
            visitMinThreshold={0}
            innerColor={row.color}
        />
    ),
    sorter: (a, b) => {
        return (a.previousShare ?? 0) - (b.previousShare ?? 0);
    },
    defaultSortOrder: "descend",
});

const volumeShare = (from?: string, to?: string, isWindow = false): KeywordTrendsTableColumn => ({
    dataIndex: "volumeShare",
    title:
        from !== undefined && to !== undefined
            ? translate("demand.analysis.search.trends.breakdown.volume.title", { from, to })
            : translate("demand.analysis.search.trends.breakdown.volume.share"),
    tooltipTitle: translate("demand.analysis.search.trends.breakdown.volume.share"),
    get tooltipSubtitle() {
        return translate(
            isWindow
                ? "demand.analysis.search.trends.breakdown.volume.share.daily.tooltip"
                : "demand.analysis.search.trends.breakdown.volume.share.tooltip",
        );
    },
    render: (value, row) => (
        <TrafficShareWithVisits
            row={row}
            value={value}
            totalVisitsRowProp="monthlyVolume"
            visitMinThreshold={0}
            innerColor={row.color}
        />
    ),
    sorter: (a, b) => {
        return a.volumeShare - b.volumeShare;
    },
    defaultSortOrder: "descend",
});

const getTotalVolumeChange = (
    periodOverPeriodType: IPeriodOverPeriodCompareType | null,
    from: string,
    to: string,
    compareFrom?: string,
    compareTo?: string,
): KeywordTrendsTableColumn => ({
    dataIndex: "volumeChange",
    get title() {
        return translate(
            periodOverPeriodType === PeriodOverPeriodService.COMPARE_TYPES.PERIOD
                ? "search.trends.breakdown.change.period.over.period"
                : "search.trends.breakdown.change.year.over.year",
        );
    },
    tooltipTitle: translate(
        periodOverPeriodType === PeriodOverPeriodService.COMPARE_TYPES.PERIOD
            ? "search.trends.breakdown.change.period.over.period"
            : "search.trends.breakdown.change.year.over.year",
    ),
    tooltipSubtitle:
        compareFrom !== undefined && compareTo !== undefined
            ? translate("demand.analysis.search.trends.breakdown.total.volume.change.tooltip.pop", {
                  from,
                  to,
                  compareFrom,
                  compareTo,
              })
            : translate("demand.analysis.search.trends.breakdown.total.volume.change.tooltip", { from, to }),
    render: (value, row) => {
        return (
            <ChangePercentage
                row={row}
                value={value}
            />
        );
    },
    sorter: (a, b) => {
        return a.volumeChange - b.volumeChange;
    },
    align: "right",
});

export const getDefaultColumns = (props: {
    periodOverPeriodType: IPeriodOverPeriodCompareType | null;
    from: string;
    to: string;
    compareFrom?: string;
    compareTo?: string;
    isWindow?: boolean;
}) => {
    const from = formatDate(props.from, shortMonthAndYear);
    const to = formatDate(props.to, shortMonthAndYear);
    const isWindow = props.isWindow ?? false;

    if (props.periodOverPeriodType != null && props.compareFrom && props.compareTo) {
        const compareFrom = formatDate(props.compareFrom, shortMonthAndYear);
        const compareTo = formatDate(props.compareTo, shortMonthAndYear);

        return [
            keywordGroupName,
            previousPeriodVolumeShare(compareFrom, compareTo),
            volumeShare(from, to, isWindow),
            getTotalVolumeChange(props.periodOverPeriodType, from, to, compareFrom, compareTo),
        ];
    }

    return [
        keywordGroupName,
        volumeShare(undefined, undefined, isWindow),
        getTotalVolumeChange(props.periodOverPeriodType, from, to),
    ];
};
